import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  analyticsConfigMutation,
  analyticsConfigQuery,
  executiveAnalyticsConfigMutation,
  executiveAnalyticsConfigQuery,
  orScheduleAnalyticsConfigMutation,
  orScheduleAnalyticsConfigQuery,
} from '@/graph/dashboard';
import { useScope } from '@/hooks/useScope';
import { useDebugMode } from '@/hooks/useDebugMode';
import toArray from 'lodash/toArray';
import get from 'lodash/get';
import IconButton from '@material-ui/core/IconButton';
import SettingsIcon from '@material-ui/icons/Settings';
import { Box, Dialog, DialogContent, DialogTitle, Tab, Tabs } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import AnalyticsConfigInputV1 from './AnalyticsConfigInputV1';
import AnalyticsConfigInputV2, {
  AverageCleaningTimeConfiguration,
  AveragePreOpTimeConfiguration,
  AverageReadyForAnesthesiaToReadyForORConfiguration,
  AverageReadyForORtoPreOpExitConfiguration,
  AverageWaitingRoomTimeConfiguration,
  TotalPatientsReceivedConfiguration,
  AverageORTimeConfiguration,
  AverageClosingTimeConfiguration,
  AveragePopiTimeConfiguration,
  AveragePacuTimeConfiguration,
  AveragePostOpTimeConfiguration,
  PacuInToChairTimeConfiguration,
  PacuInToPTStartTimeConfiguration,
  PacuInToPTCompleteTimeConfiguration,
} from './AnalyticsConfigInputV2';
import OrScheduleConfigInput from './OrScheduleConfigInput';
import { CenteredSpinner } from '@/se/components/Spinner';
import Form from '@/se/components/Form';
import useModal from '../../../../hooks/useModal';

interface ConfigurationProps {
  title?: string;
  configType?:
    | 'totalPatientsReceived'
    | 'averageWaitingRoomTime'
    | 'averagePreOPTime'
    | 'averageReadyForAnesthesiatoReadyForOR'
    | 'averageReadyForORtoPreOpExit'
    | 'averageORTime'
    | 'averageCleaningTime'
    | 'averageClosingTime'
    | 'averagePopiTime'
    | 'averagePACUTime'
    | 'averagePostOpTime'
    | 'pacuInToChairTime'
    | 'pacuInToPTStartTime'
    | 'pacuInToPTCompleteTime';
  size?: 'normal' | 'small';
}

const Configuration = ({ title, configType, size = 'normal' }: ConfigurationProps) => {
  const { open, openModal, closeModal } = useModal();
  const [selectedTab, setSelectedTab] = React.useState(0);
  const { canBypassPermissions } = useDebugMode();
  const [mutateRegular] = useMutation(analyticsConfigMutation);
  const [mutateExecutive] = useMutation(executiveAnalyticsConfigMutation);
  const [mutateOrSchedule] = useMutation(orScheduleAnalyticsConfigMutation);
  const { data: regularData, loading: regularLoading } = useQuery(analyticsConfigQuery, {
    fetchPolicy: 'cache-first',
    skip: !open || selectedTab !== 0,
  });
  const { data: executiveData, loading: executiveLoading } = useQuery(executiveAnalyticsConfigQuery, {
    fetchPolicy: 'cache-first',
    skip: !open || selectedTab !== 1,
  });
  const { data: orScheduleData, loading: orScheduleLoading } = useQuery(orScheduleAnalyticsConfigQuery, {
    fetchPolicy: 'cache-first',
    skip: !open || selectedTab !== 2,
  });
  const scope = useScope();
  const analyticsV2 = scope?.hospital?.modules?.analyticsV2;

  const handleRegularSubmit = async values => {
    const monthlyReportRecipients = toArray(get(values, 'monthlyReportRecipients'));
    const weeklyReportRecipients = toArray(get(values, 'weeklyReportRecipients'));

    const totalPatientsThreshold = get(values, 'totalPatientsThreshold');
    const waitingRoomTimeThreshold = get(values, 'waitingRoomTimeThreshold');
    const preopTimeThreshold = get(values, 'preopTimeThreshold');
    const readyForAnesthesiaThreshold = get(values, 'readyForAnesthesiaThreshold');
    const readyForORThreshold = get(values, 'readyForORThreshold');
    // const orUtilThreshold = get(values, 'orUtilThreshold');
    const orTimeThreshold = get(values, 'orTimeThreshold');
    const pacuTimeThreshold = get(values, 'pacuTimeThreshold');
    const cleaningTimeThreshold = get(values, 'cleaningTimeThreshold');
    const closingTimeThreshold = get(values, 'closingTimeThreshold');
    const popiTimeThreshold = get(values, 'popiTimeThreshold');
    const postOpTimeThreshold = get(values, 'postOpTimeThreshold');
    const pipoMinThreshold = get(values, 'pipoMinThreshold');
    const pipoMaxThreshold = get(values, 'pipoMaxThreshold');
    const popiMinThreshold = get(values, 'popiMinThreshold');
    const popiMaxThreshold = get(values, 'popiMaxThreshold');
    const wrMinThreshold = get(values, 'wrMinThreshold');
    const wrMaxThreshold = get(values, 'wrMaxThreshold');
    const preOpMinThreshold = get(values, 'preOpMinThreshold');
    const preOpMaxThreshold = get(values, 'preOpMaxThreshold');
    const cleaningMinThreshold = get(values, 'cleaningMinThreshold');
    const cleaningMaxThreshold = get(values, 'cleaningMaxThreshold');
    const pacuMinThreshold = get(values, 'pacuMinThreshold');
    const pacuMaxThreshold = get(values, 'pacuMaxThreshold');
    const postOpMinThreshold = get(values, 'postOpMinThreshold');
    const postOpMaxThreshold = get(values, 'postOpMaxThreshold');
    const preOpToDischargedMinThreshold = get(values, 'preOpToDischargedMinThreshold');
    const preOpToDischargedMaxThreshold = get(values, 'preOpToDischargedMaxThreshold');
    const inOrMinThreshold = get(values, 'inOrMinThreshold');
    const inOrMaxThreshold = get(values, 'inOrMaxThreshold');
    const inSurgeryMinThreshold = get(values, 'inSurgeryMinThreshold');
    const inSurgeryMaxThreshold = get(values, 'inSurgeryMaxThreshold');
    const closingMinThreshold = get(values, 'closingMinThreshold');
    const closingMaxThreshold = get(values, 'closingMaxThreshold');
    const procedureCompleteMinThreshold = get(values, 'procedureCompleteMinThreshold');
    const procedureCompleteMaxThreshold = get(values, 'procedureCompleteMaxThreshold');
    const orReadyMinThreshold = get(values, 'orReadyMinThreshold');
    const orReadyMaxThreshold = get(values, 'orReadyMaxThreshold');
    const piToStartMinThreshold = get(values, 'piToStartMinThreshold');
    const piToStartMaxThreshold = get(values, 'piToStartMaxThreshold');
    const poToCutMinThreshold = get(values, 'poToCutMinThreshold');
    const poToCutMaxThreshold = get(values, 'poToCutMaxThreshold');
    const startToCloseMinThreshold = get(values, 'startToCloseMinThreshold');
    const startToCloseMaxThreshold = get(values, 'startToCloseMaxThreshold');
    const physicianPopiMinThreshold = get(values, 'physicianPopiMinThreshold');
    const physicianPopiMaxThreshold = get(values, 'physicianPopiMaxThreshold');
    const fcotsGracePeriod = get(values, 'fcotsGracePeriod');
    const piToPhyiMinThreshold = get(values, 'piToPhyiMinThreshold');
    const piToPhyiMaxThreshold = get(values, 'piToPhyiMaxThreshold');
    const closingCompleteToPatientOutMinThreshold = get(values, 'closingCompleteToPatientOutMinThreshold');
    const closingCompleteToPatientOutMaxThreshold = get(values, 'closingCompleteToPatientOutMaxThreshold');
    const samePhysicianORTurnover = get(values, 'samePhysicianORTurnover');
    const wrInToDischargeMinThreshold = get(values, 'wrInToDischargeMinThreshold');
    const wrInToDischargeMaxThreshold = get(values, 'wrInToDischargeMaxThreshold');
    const fcotsDelayMinThreshold = get(values, 'fcotsDelayMinThreshold');
    const fcotsDelayMaxThreshold = get(values, 'fcotsDelayMaxThreshold');
    const rtorMinThreshold = get(values, 'rtorMinThreshold');
    const rtorMaxThreshold = get(values, 'rtorMaxThreshold');
    const timeToChairThreshold = get(values, 'timeToChairThreshold');
    const ptStartTimeThreshold = get(values, 'ptStartTimeThreshold');
    const ptCompleteTimeThreshold = get(values, 'ptCompleteTimeThreshold');

    const result = await mutateRegular({
      variables: {
        monthlyReportRecipients,
        weeklyReportRecipients,
        totalPatientsThreshold,
        waitingRoomTimeThreshold,
        preopTimeThreshold,
        readyForAnesthesiaThreshold,
        readyForORThreshold,
        // orUtilThreshold,
        orTimeThreshold,
        pacuTimeThreshold,
        cleaningTimeThreshold,
        closingTimeThreshold,
        popiTimeThreshold,
        postOpTimeThreshold,
        pipoMinThreshold,
        pipoMaxThreshold,
        popiMinThreshold,
        popiMaxThreshold,
        wrMinThreshold,
        wrMaxThreshold,
        preOpMinThreshold,
        preOpMaxThreshold,
        cleaningMinThreshold,
        cleaningMaxThreshold,
        pacuMinThreshold,
        pacuMaxThreshold,
        postOpMinThreshold,
        postOpMaxThreshold,
        preOpToDischargedMinThreshold,
        preOpToDischargedMaxThreshold,
        inOrMinThreshold,
        inOrMaxThreshold,
        inSurgeryMinThreshold,
        inSurgeryMaxThreshold,
        closingMinThreshold,
        closingMaxThreshold,
        procedureCompleteMinThreshold,
        procedureCompleteMaxThreshold,
        orReadyMinThreshold,
        orReadyMaxThreshold,
        piToStartMinThreshold,
        piToStartMaxThreshold,
        poToCutMinThreshold,
        poToCutMaxThreshold,
        startToCloseMinThreshold,
        startToCloseMaxThreshold,
        physicianPopiMinThreshold,
        physicianPopiMaxThreshold,
        fcotsGracePeriod,
        piToPhyiMinThreshold,
        piToPhyiMaxThreshold,
        closingCompleteToPatientOutMinThreshold,
        closingCompleteToPatientOutMaxThreshold,
        samePhysicianORTurnover,
        wrInToDischargeMinThreshold,
        wrInToDischargeMaxThreshold,
        fcotsDelayMinThreshold,
        fcotsDelayMaxThreshold,
        rtorMinThreshold,
        rtorMaxThreshold,
        timeToChairThreshold,
        ptStartTimeThreshold,
        ptCompleteTimeThreshold,
      },
      refetchQueries: [{ query: analyticsConfigQuery }],
    });

    if (get(result, 'data.updateAnalyticsConfiguration')) {
      closeModal();
    }
  };

  const handleExecutiveSubmit = async values => {
    const monthlyReportRecipients = toArray(get(values, 'monthlyReportRecipients'));
    const weeklyReportRecipients = toArray(get(values, 'weeklyReportRecipients'));
    const variables: any = {
      monthlyReportRecipients,
      weeklyReportRecipients,
    };
    if (canBypassPermissions) {
      variables.weeklyReportDay = Number(get(values, 'weeklyReportDay') ?? 1);
      variables.weeklyReportHour = Number(get(values, 'weeklyReportHour') ?? 8);
      variables.monthlyReportDay = Number(get(values, 'monthlyReportDay') ?? 1);
      variables.monthlyReportHour = Number(get(values, 'monthlyReportHour') ?? 8);
    }
    const result = await mutateExecutive({
      variables,
      refetchQueries: [{ query: executiveAnalyticsConfigQuery }],
    });
    if (get(result, 'data.updateExecutiveAnalyticsConfiguration')) {
      closeModal();
    }
  };

  const handleOrScheduleSubmit = async values => {
    const dailyReportRecipients = toArray(get(values, 'dailyReportRecipients'));
    const reportHour = get(values, 'reportHour');
    const result = await mutateOrSchedule({
      variables: {
        dailyReportRecipients,
        reportHour: reportHour != null ? Number(reportHour) : null,
      },
      refetchQueries: [{ query: orScheduleAnalyticsConfigQuery }],
    });
    if (get(result, 'data.updateOrScheduleAnalyticsConfiguration')) {
      closeModal();
    }
  };

  const configTypeMap = {
    totalPatientsReceived: TotalPatientsReceivedConfiguration,
    averageWaitingRoomTime: AverageWaitingRoomTimeConfiguration,
    averagePreOPTime: AveragePreOpTimeConfiguration,
    averageReadyForAnesthesiatoReadyForOR: AverageReadyForAnesthesiaToReadyForORConfiguration,
    averageReadyForORtoPreOpExit: AverageReadyForORtoPreOpExitConfiguration,
    averageORTime: AverageORTimeConfiguration,
    averageCleaningTime: AverageCleaningTimeConfiguration,
    averageClosingTime: AverageClosingTimeConfiguration,
    averagePopiTime: AveragePopiTimeConfiguration,
    averagePACUTime: AveragePacuTimeConfiguration,
    averagePostOpTime: AveragePostOpTimeConfiguration,
    pacuInToChairTime: PacuInToChairTimeConfiguration,
    pacuInToPTStartTime: PacuInToPTStartTimeConfiguration,
    pacuInToPTCompleteTime: PacuInToPTCompleteTimeConfiguration,
  };

  const StickyFooter = ({ busy, disabled, onCancel }) => (
    <Box display="flex" pt={2} pb={1}>
      <Button type="submit" variant="contained" color="primary" disabled={disabled || busy}>
        Save
      </Button>
      <Button onClick={onCancel}>Cancel</Button>
    </Box>
  );

  return (
    <Box>
      <IconButton onClick={openModal} aria-label="settings" size={size === 'small' ? 'small' : 'medium'}>
        <SettingsIcon fontSize={size === 'small' ? 'small' : 'medium'} />
      </IconButton>
      <Dialog
        open={open}
        onClose={closeModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            height: '75vh',
            maxHeight: '75vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle>{title || 'Analytics Global Configuration'}</DialogTitle>
        {!regularLoading && !executiveLoading && !orScheduleLoading ? (
          <>
            {!configType && (
              <Box px={3} pt={1}>
                <Tabs
                  value={selectedTab}
                  onChange={(_, value) => setSelectedTab(value)}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  <Tab label="Regular Reports" />
                  <Tab label="Executive Reports" />
                  <Tab label="OR Schedule Reports" />
                </Tabs>
              </Box>
            )}
            <DialogContent
              style={{ paddingTop: 16, overflow: 'hidden', flex: 1, display: 'flex', minHeight: 0, width: '100%' }}
            >
              {selectedTab === 0 && analyticsV2 && (
                <Form
                  autoFocus
                  initialValue={get(regularData, 'analyticsConfiguration')}
                  input={
                    configType
                      ? configTypeMap[configType]
                      : analyticsV2
                        ? AnalyticsConfigInputV2
                        : AnalyticsConfigInputV1
                  }
                  label={'Save'}
                  onSubmit={handleRegularSubmit}
                  onCancel={closeModal}
                  Footer={StickyFooter}
                  fixedFooter
                  inputProps={{ style: { paddingBottom: 8 } }}
                />
              )}
              {selectedTab === 1 && (
                <Form
                  autoFocus
                  initialValue={get(executiveData, 'executiveAnalyticsConfiguration')}
                  input={AnalyticsConfigInputV1}
                  label={'Save'}
                  onSubmit={handleExecutiveSubmit}
                  onCancel={closeModal}
                  Footer={StickyFooter}
                  fixedFooter
                  inputProps={{ style: { paddingBottom: 8 } }}
                />
              )}
              {selectedTab === 2 && (
                <Form
                  autoFocus
                  initialValue={get(orScheduleData, 'orScheduleAnalyticsConfiguration')}
                  input={OrScheduleConfigInput}
                  label={'Save'}
                  onSubmit={handleOrScheduleSubmit}
                  onCancel={closeModal}
                  Footer={StickyFooter}
                  fixedFooter
                  inputProps={{ style: { paddingBottom: 8 } }}
                />
              )}
            </DialogContent>
          </>
        ) : (
          <CenteredSpinner />
        )}
      </Dialog>
    </Box>
  );
};

export default Configuration;
